import type { ReactNode } from 'react'

import { useMemo, useState } from 'react'
import { useElementWidth } from '@/hooks/use-element-width'
import { niceDomain, scaleLinear } from '@/components/charts/scales'
import { formatNumber } from '@/lib/market/format'
import Legend from '@/components/charts/legend'

interface HistogramProps {
    edges: number[]
    counts: number[]
    curve?: number[]
    height?: number
    unit?: string
    ariaLabel: string
    countLabel?: string
    curveLabel?: string
}

const LEFT = 40
const RIGHT = 10
const TOP = 10
const BOTTOM = 26

const Histogram = ({ edges, counts, curve, height = 220, unit = '%', ariaLabel, countLabel = 'Sessions observed', curveLabel = 'Normal distribution' }: HistogramProps): ReactNode => {
    const [ref, width] = useElementWidth<HTMLDivElement>(520)
    const [active, setActive] = useState<number | null>(null)
    const bins = counts.length
    const plotWidth = Math.max(60, width - LEFT - RIGHT)
    const plotHeight = height - TOP - BOTTOM
    const step = plotWidth / Math.max(1, bins)

    const domain = useMemo(() => niceDomain(0, Math.max(1, ...counts, ...(curve ?? [])), 4, 0.05, true), [counts, curve])
    const y = scaleLinear([domain.min, domain.max], [TOP + plotHeight, TOP])
    const x0 = edges[0] ?? 0
    const x1 = edges[edges.length - 1] ?? 1
    const xScale = scaleLinear([x0, x1], [LEFT, LEFT + plotWidth])
    const barWidth = Math.max(1, Math.min(24, step - 2))
    const radius = barWidth >= 6 ? 4 : 0

    const labelEvery = Math.max(1, Math.ceil(bins / Math.max(2, Math.floor(plotWidth / 56))))
    const curvePath = curve
        ? curve.map((value, index) => `${index === 0 ? 'M' : 'L'}${(LEFT + (index + 0.5) * step).toFixed(1)},${y(value).toFixed(1)}`).join('')
        : ''

    const label = (index: number): string => `${formatNumber(edges[index], 1)}${unit} to ${formatNumber(edges[index + 1], 1)}${unit}`

    return (
        <div>
            {curve && (
                <Legend
                    className='mb-2'
                    items={[
                        { key: 'observed', label: countLabel, color: 'var(--series-1)', shape: 'rect' },
                        { key: 'normal', label: curveLabel, color: 'var(--series-2)', shape: 'line' }
                    ]}
                />
            )}

            <div ref={ref} className='relative w-full' role='group' aria-label={ariaLabel}>
                <svg width='100%' height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio='xMinYMin meet' aria-hidden='true' className='block overflow-visible'>
                    {domain.ticks.map(tick => (
                        <g key={tick}>
                            <line x1={LEFT} x2={LEFT + plotWidth} y1={y(tick)} y2={y(tick)} stroke='var(--grid)' shapeRendering='crispEdges' />
                            <text x={LEFT - 8} y={y(tick)} dy='0.32em' textAnchor='end' fontSize={11} fill='var(--muted-foreground)'>{formatNumber(tick)}</text>
                        </g>
                    ))}

                    {x0 < 0 && x1 > 0 && (
                        <line x1={xScale(0)} x2={xScale(0)} y1={TOP} y2={TOP + plotHeight} stroke='var(--axis)' strokeWidth={1} />
                    )}

                    {counts.map((count, index) => {
                        const left = LEFT + index * step + (step - barWidth) / 2
                        const top = y(count)
                        const bottom = y(0)
                        const barHeight = Math.max(0, bottom - top)
                        const r = Math.min(radius, barHeight, barWidth / 2)
                        const path = count <= 0
                            ? ''
                            : r >= 1
                                ? `M${left},${bottom}V${top + r}Q${left},${top} ${left + r},${top}H${left + barWidth - r}Q${left + barWidth},${top} ${left + barWidth},${top + r}V${bottom}Z`
                                : `M${left},${bottom}V${top}H${left + barWidth}V${bottom}Z`

                        return (
                            <g key={index}>
                                {path && <path d={path} fill='var(--series-1)' fillOpacity={active === null || active === index ? 0.9 : 0.45} />}
                                <rect
                                    x={LEFT + index * step}
                                    y={TOP}
                                    width={step}
                                    height={plotHeight}
                                    fill='transparent'
                                    onPointerEnter={() => setActive(index)}
                                    onPointerLeave={() => setActive(null)}
                                />
                            </g>
                        )
                    })}

                    {curvePath && <path d={curvePath} fill='none' stroke='var(--series-2)' strokeWidth={2} strokeLinejoin='round' pointerEvents='none' />}

                    <line x1={LEFT} x2={LEFT + plotWidth} y1={TOP + plotHeight} y2={TOP + plotHeight} stroke='var(--axis)' shapeRendering='crispEdges' />

                    {edges.map((edge, index) => (index % labelEvery === 0 ? (
                        <text key={`edge-${index}`} x={LEFT + index * step} y={height - 8} textAnchor='middle' fontSize={11} fill='var(--muted-foreground)'>
                            {formatNumber(edge, Math.abs(edge) < 10 ? 1 : 0)}{unit}
                        </text>
                    ) : null))}
                </svg>

                {active !== null && (
                    <div
                        className='pointer-events-none absolute top-0 z-10 rounded-lg border border-border bg-popover/95 px-3 py-2 text-xs shadow-xl'
                        style={{ left: `${Math.min(70, Math.max(0, ((LEFT + active * step) / width) * 100))}%` }}
                    >
                        <p className='font-medium text-muted-foreground'>{label(active)}</p>
                        <p className='mt-1 flex items-center justify-between gap-4'>
                            <span className='text-muted-foreground'>{countLabel}</span>
                            <span className='font-medium tabular-nums text-foreground'>{counts[active]}</span>
                        </p>
                        {curve && (
                            <p className='flex items-center justify-between gap-4'>
                                <span className='text-muted-foreground'>Expected if normal</span>
                                <span className='font-medium tabular-nums text-foreground'>{formatNumber(curve[active], 1)}</span>
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default Histogram
